import { useState, useEffect, useCallback, useMemo } from 'react'
import { useMenuData } from '@/hooks/useMenuData'
import { useLanguage, commonTranslations } from '@/hooks/useLanguage'
import { useCart } from '@/hooks/useCart'
import { useSearch } from '@/hooks/useSearch'
import { useFavorites } from '@/hooks/useFavorites'
import { useTableNumber } from '@/hooks/useTableNumber'
import { ThemeProvider } from '@/components/common/ThemeProvider'
import { Header, HeaderSpacer } from '@/components/layout/Header'
import { Footer } from '@/components/layout/Footer'
import { StickyBar } from '@/components/layout/StickyBar'
import { CategoryTabs } from '@/components/menu/CategoryTabs'
import { ItemGrid } from '@/components/menu/ItemGrid'
import { SearchBar } from '@/components/menu/SearchBar'
import { VariantSelector } from '@/components/menu/VariantSelector'
import { ModifierSelector } from '@/components/menu/ModifierSelector'
import { CartButton } from '@/components/cart/CartButton'
import { CartSheet } from '@/components/cart/CartSheet'
import { Loading } from '@/components/common/Loading'
import { Error } from '@/components/common/Error'
import type { MenuItem, Variant, CartItem } from '@/types'

// ============================================================================
// Menu Page Component
// ============================================================================

export function MenuPage() {
  const { config, menuData, isLoading, error, reload } = useMenuData()
  const { language, setLanguage, isRTL, textDirection } = useLanguage(
    config?.languages.default || 'fr'
  )
  const { cart, addItem, clearCart, updateQuantity, removeItem, itemCount, subtotal } = useCart()
  const { query, setQuery, results, resultCount, clearSearch } = useSearch(
    menuData?.categories || [],
    language
  )
  const { isFavorite, toggleFavorite } = useFavorites()
  const { tableNumber } = useTableNumber()

  const [activeCategory, setActiveCategory] = useState<string | undefined>(undefined)
  const [isCartOpen, setIsCartOpen] = useState(false)
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null)
  const [cartAnimate, setCartAnimate] = useState(false)
  const [photoLightbox, setPhotoLightbox] = useState<MenuItem | null>(null)

  // Track page view for analytics (demo mode - just logs)
  useEffect(() => {
    if (config?.features.analytics) {
      const today = new Date().toISOString().split('T')[0]
      try {
        const key = `qr-menu-views-${today}`
        const current = parseInt(localStorage.getItem(key) || '0', 10)
        localStorage.setItem(key, (current + 1).toString())
      } catch (e) {
        // Ignore storage errors
      }
    }
  }, [config])

  // Set first category as active
  useEffect(() => {
    if (menuData && menuData.categories.length > 0 && !activeCategory) {
      setActiveCategory(menuData.categories[0].id)
    }
  }, [menuData, activeCategory])

  // Handle add item - opens variant selector if needed
  const handleAddItem = useCallback(
    (item: MenuItem) => {
      const hasVariants = item.variants && item.variants.length > 0
      const hasModifiers = item.modifiers && item.modifiers.length > 0

      if (hasVariants) {
        setSelectedItem(item)
      } else if (hasModifiers) {
        setSelectedItem(item)
      } else {
        // Simple item - add directly
        addItem({
          itemId: item.id,
          name:
            language === 'fr'
              ? item.name_fr
              : language === 'ar'
              ? item.name_ar
              : item.name_en,
          modifiers: [],
          price: item.price,
        })
        triggerCartAnimation()
      }
    },
    [addItem, language]
  )

  // Trigger cart animation
  const triggerCartAnimation = useCallback(() => {
    setCartAnimate(false)
    setTimeout(() => setCartAnimate(true), 10)
  }, [])

  // Handle variant selection confirm
  const handleVariantConfirm = useCallback(
    (variant: Variant | null, selectedModifiers: Array<{ id: string; optionId?: string; name: string; price: number }>) => {
      if (!selectedItem) return

      const itemName =
        language === 'fr'
          ? selectedItem.name_fr
          : language === 'ar'
          ? selectedItem.name_ar
          : selectedItem.name_en

      // Calculate price
      let price = selectedItem.price
      if (variant) {
        if (selectedItem.variant_pricing === 'absolute' && variant.price !== undefined) {
          price = variant.price
        } else if (selectedItem.variant_pricing === 'relative' && variant.price_adjustment !== undefined) {
          price = (selectedItem.base_price || selectedItem.price) + variant.price_adjustment
        }
      }

      const cartVariant = variant
        ? {
            id: variant.id,
            name:
              language === 'fr'
                ? variant.name_fr
                : language === 'ar'
                ? variant.name_ar
                : variant.name_en,
            price: 0,
          }
        : undefined

      const cartItem: Omit<CartItem, 'quantity'> = {
        itemId: selectedItem.id,
        name: itemName,
        variant: cartVariant,
        modifiers: selectedModifiers,
        price,
      }

      addItem(cartItem)
      triggerCartAnimation()
      setSelectedItem(null)
    },
    [selectedItem, addItem, language]
  )

  // Handle modifier-only confirm
  const handleModifierConfirm = useCallback(
    (selectedModifiers: Array<{ id: string; optionId?: string; name: string; price: number }>) => {
      if (!selectedItem) return

      const itemName =
        language === 'fr'
          ? selectedItem.name_fr
          : language === 'ar'
          ? selectedItem.name_ar
          : selectedItem.name_en

      const modifiersTotal = selectedModifiers.reduce((sum, mod) => sum + mod.price, 0)

      addItem({
        itemId: selectedItem.id,
        name: itemName,
        modifiers: selectedModifiers,
        price: selectedItem.price + modifiersTotal,
      })

      triggerCartAnimation()
      setSelectedItem(null)
    },
    [selectedItem, addItem, language]
  )

  // Loading state
  if (isLoading) {
    return <Loading language={language} />
  }

  // Error state
  if (error || !config || !menuData) {
    return (
      <Error
        message={error || undefined}
        onRetry={reload}
        language={language}
      />
    )
  }

  const categoriesToDisplay = query.trim() ? results : menuData.categories

  // Get available languages
  const availableLanguages = config.features.languageToggle
    ? config.languages.enabled
    : [language]

  // Determine if item has variants or modifiers
  const selectedItemHasVariants =
    selectedItem?.variants && selectedItem.variants.length > 0

  return (
    <ThemeProvider theme={config.theme}>
      <div className="min-h-screen bg-background flex flex-col" dir={textDirection}>
        {/* Header */}
        <Header />

        <HeaderSpacer />

        {/* Combined sticky toolbar: search + category tabs stick together */}
        <div className="sticky top-16 sm:top-20 z-30">
          {config.features.search && (
            <div className="bg-background/95 backdrop-blur-sm px-4 py-2.5 border-b border-secondary/20">
              <SearchBar
                query={query}
                onQueryChange={setQuery}
                onClear={clearSearch}
                resultCount={query.trim() ? resultCount : undefined}
                language={language}
                isRTL={isRTL}
              />
            </div>
          )}

          {/* Category Tabs (hidden while searching) */}
          {!query && (
            <CategoryTabs
              categories={menuData.categories}
              language={language}
              isRTL={isRTL}
              activeCategory={activeCategory}
              onCategoryChange={setActiveCategory}
            />
          )}
        </div>

        {/* Items Grid */}
        <main className="flex-1 px-4 py-5 max-w-7xl mx-auto w-full">
          <ItemGrid
            categories={categoriesToDisplay}
            language={language}
            isRTL={isRTL}
            hasPhotos={config.features.photos}
            hasCart={config.features.cart && config.features.whatsappOrdering}
            onAddItem={handleAddItem}
            onToggleFavorite={config.features.favorites ? toggleFavorite : undefined}
            isFavorite={config.features.favorites ? isFavorite : undefined}
            onViewPhoto={config.features.photos ? setPhotoLightbox : undefined}
          />
        </main>

        {/* Footer */}
        <Footer />

        {/* Mobile Sticky Bar (call + WhatsApp + cart) */}
        <StickyBar onCartClick={() => setIsCartOpen(true)} />

        {/* Cart Sheet */}
        {config.features.cart && config.features.whatsappOrdering && (
          <CartSheet
            cart={cart ? { ...cart, tableNumber: tableNumber || cart.tableNumber } : null}
            businessName={config.business.name}
            whatsapp={config.contact.whatsapp}
            language={language}
            isOpen={isCartOpen}
            onClose={() => setIsCartOpen(false)}
            onUpdateQuantity={updateQuantity}
            onRemoveItem={removeItem}
            onClear={clearCart}
          />
        )}

        {/* Variant/Modifier Selector Modal */}
        {selectedItem && selectedItemHasVariants && (
          <VariantSelector
            itemId={selectedItem.id}
            itemName={
              language === 'fr'
                ? selectedItem.name_fr
                : language === 'ar'
                ? selectedItem.name_ar
                : selectedItem.name_en
            }
            basePrice={selectedItem.base_price || selectedItem.price}
            variants={selectedItem.variants || []}
            variantPricing={selectedItem.variant_pricing}
            modifiers={selectedItem.modifiers}
            language={language}
            isRTL={isRTL}
            onAdd={handleVariantConfirm}
            onClose={() => setSelectedItem(null)}
          />
        )}

        {selectedItem && !selectedItemHasVariants && selectedItem.modifiers && selectedItem.modifiers.length > 0 && (
          <ModifierSelector
            itemId={selectedItem.id}
            itemName={
              language === 'fr'
                ? selectedItem.name_fr
                : language === 'ar'
                ? selectedItem.name_ar
                : selectedItem.name_en
            }
            basePrice={selectedItem.price}
            modifiers={selectedItem.modifiers}
            language={language}
            isRTL={isRTL}
            onAdd={handleModifierConfirm}
            onClose={() => setSelectedItem(null)}
          />
        )}

        {/* Photo Lightbox */}
        {photoLightbox && photoLightbox.photo && (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 animate-fade-in"
            onClick={() => setPhotoLightbox(null)}
          >
            <button
              className="absolute top-4 right-4 p-2 rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors"
              onClick={() => setPhotoLightbox(null)}
              aria-label="Close"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            <img
              src={photoLightbox.photo}
              alt={
                language === 'fr'
                  ? photoLightbox.name_fr
                  : language === 'ar'
                  ? photoLightbox.name_ar
                  : photoLightbox.name_en
              }
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </div>
    </ThemeProvider>
  )
}

export default MenuPage
